import { c as _c } from "react/compiler-runtime";
import { addToast } from "@heroui/react";
import { ExternalLink } from "lucide-react";
import type { Chain } from "viem";

/**
 * Configuration for transaction toast notifications
 */
export interface TransactionToastConfig {
  /** 
   * Action description (e.g., "Transfer LP tokens", "Swap tokens", "Add liquidity")
   * This will be used in loading, success, and error messages
   */
  action: string;

  /** 
   * Optional chain information for block explorer link 
   */
  chain?: {
    id: number;
    blockExplorers?: {
      default: {
        url: string;
      };
    };
  };

  /**
   * Optional custom loading message (defaults to "{action}...")
   */
  loadingMessage?: string;

  /**
   * Optional custom success message (defaults to "{action} successful!")
   */
  successMessage?: string;

  /**
   * Optional custom error message (defaults to "{action} failed")
   */
  errorMessage?: string;

  /**
   * Whether to show timeout progress bar
   */
  showProgress?: boolean;

  /**
   * Timeout in milliseconds (default: 6000 for success, 10000 for error)
   */
  timeout?: number;
}

/**
 * Get block explorer URL for a transaction hash
 */
function getExplorerUrl(hash: string, chain?: TransactionToastConfig["chain"]): string | null {
  if (!chain?.blockExplorers?.default?.url) return null;
  return `${chain.blockExplorers.default.url}/tx/${hash}`;
}

/**
 * Create transaction link component for toast description
 */
function TransactionLink(t0) {
  const $ = _c(10);
  const {
    hash,
    explorerUrl
  } = t0;
  let t1;
  if ($[0] !== hash) {
    t1 = hash.slice(0, 6);
    $[0] = hash;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hash) {
    t2 = hash.slice(-4);
    $[2] = hash;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const shortHash = `${t1}...${t2}`;
  if (!explorerUrl) {
    let t3;
    if ($[4] !== shortHash) {
      t3 = <span className="font-mono text-sm">{shortHash}</span>;
      $[4] = shortHash;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ExternalLink className="h-3 w-3" />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== explorerUrl || $[8] !== shortHash) {
    t4 = <a href={explorerUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-mono text-sm hover:underline" onClick={_temp}>{shortHash}{t3}</a>;
    $[7] = explorerUrl;
    $[8] = shortHash;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/**
 * Show a transaction toast that tracks a promise (contract write + confirmation)
 * 
 * This creates a toast with loading state that automatically updates to success/error
 * based on the promise resolution.
 * 
 * @example
 * ```typescript
 * const txPromise = writeContract(...).then(waitForReceipt);
 * 
 * showTransactionToast(txPromise, {
 *   action: "Transfer LP tokens",
 *   chain: currentChain,
 * });
 * ```
 */
function _temp(e) {
  return e.stopPropagation();
}
export function showTransactionToast<T extends {
  hash?: string;
}>(promise: Promise<T>, config: TransactionToastConfig): Promise<T> {
  const {
    action,
    chain,
    loadingMessage,
    successMessage,
    errorMessage,
    showProgress = false,
    timeout
  } = config;

  // Show loading toast with promise
  addToast({
    title: loadingMessage || `${action}...`,
    description: "Waiting for transaction confirmation",
    severity: "default",
    promise: promise.then(result => {
      // Success - update toast
      const explorerUrl = result.hash ? getExplorerUrl(result.hash, chain) : null;
      return {
        title: successMessage || `${action} successful!`,
        description: result.hash ? <TransactionLink hash={result.hash} explorerUrl={explorerUrl} /> : undefined,
        severity: "success" as const,
        timeout: timeout || 8000,
        shouldShowTimeoutProgress: showProgress
      };
    }, error => {
      // Error - update toast
      const errorMsg = error instanceof Error ? error.message : "Unknown error";
      return {
        title: errorMessage || `${action} failed`,
        description: errorMsg,
        severity: "danger" as const,
        timeout: timeout || 10000,
        shouldShowTimeoutProgress: showProgress
      };
    }),
    loadingComponent: <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                <span>Processing transaction...</span>
            </div>,
    hideIcon: false,
    shouldShowTimeoutProgress: showProgress
  });
  return promise;
}

/**
 * Show a simple transaction loading toast
 * Use this if you need manual control over the toast updates
 * 
 * @example
 * ```typescript
 * showTransactionLoading("Swapping tokens");
 * // ... do transaction ...
 * showTransactionSuccess(hash, "Swap tokens", chain);
 * ```
 */
export function showTransactionLoading(action: string): void {
  addToast({
    title: `${action}...`,
    description: "Waiting for wallet confirmation",
    severity: "default",
    timeout: 0,
    // Don't auto-close
    hideCloseButton: true
  });
}

/**
 * Show a transaction success toast with explorer link
 */
export function showTransactionSuccess(hash: string, action: string, chain?: TransactionToastConfig["chain"], customMessage?: string): void {
  const explorerUrl = getExplorerUrl(hash, chain);
  addToast({
    title: customMessage || `${action} successful!`,
    description: <TransactionLink hash={hash} explorerUrl={explorerUrl} />,
    severity: "success",
    timeout: 8000,
    shouldShowTimeoutProgress: true
  });
}

/**
 * Show a transaction error toast
 */
export function showTransactionError(error: Error | unknown, action: string, customMessage?: string): void {
  const errorMsg = error instanceof Error ? error.message : "Unknown error";
  addToast({
    title: customMessage || `${action} failed`,
    description: errorMsg,
    severity: "danger",
    timeout: 10000,
    shouldShowTimeoutProgress: true
  });
}

/**
 * Common transaction actions for consistency
 */
export const TX_ACTIONS = {
  SWAP: "Swap tokens",
  ADD_LIQUIDITY: "Add liquidity",
  REMOVE_LIQUIDITY: "Remove liquidity",
  TRANSFER_LP: "Transfer LP tokens",
  STAKE: "Stake tokens",
  UNSTAKE: "Unstake tokens",
  CLAIM_REWARDS: "Claim rewards",
  APPROVE: "Approve tokens",
  BRIDGE: "Bridge tokens",
  MINT_NFT: "Mint NFT",
  TRANSFER_NFT: "Transfer NFT"
} as const;